import once from 'lodash.once'
import {
  Browser,
  UICorePlugin,
  template,
  type EventMap,
  type AttributeMap,
  type TemplateStringifier,
  type Strings,
  type SupportedVersion
} from '@clappr/core'
import {
  type Options,
  PLAY_IN_VISIBLE_AREA,
  UNMUTE_IN_VISIBLE_AREA
} from 'common/types/options'
import {Events} from 'common/types/events'
import type {MediaControl} from 'common/plugins/media-control'
import type {Notifier} from 'common/plugins/notifier'
import * as skins from './skins'

export class Unmute<T extends Options> extends UICorePlugin<T> {
  // if `null` — button will hide on unmute, if `number` — on timeout
  public static SHOW_TIMEOUT: null | number = null
  // if `true` — unmute button will show up on mute
  // if `false` — only on automatically play muted video
  public static SHOW_ON_MUTE = false
  public static SHOW_IF_NO_SOUND = false
  public static VOLUME = 10

  private showTimeout?: number
  private static template = ''
  private static classNames: Record<string, string> = {}
  private static holdable: true
  private static shiftable: false

  public static async preload(options: Options): Promise<Options | void> {
    if (options.unmute && !Unmute.template) {
      const skinName = options.mediaControl?.skinName
      const loadSkin = (skinName && (skins as any)[skinName]) || skins.rambler

      if (typeof loadSkin !== 'function') {
        return
      }

      const {
        default: template,
        classNames,
        holdable,
        shiftable,
        SHOW_TIMEOUT,
        SHOW_ON_MUTE,
        SHOW_IF_NO_SOUND
      } = await loadSkin()

      if (template) {
        Unmute.template = template
      }

      if (classNames) {
        Unmute.classNames = classNames
      }

      Unmute.SHOW_TIMEOUT =
        options.mediaControlVisible === false ? null : SHOW_TIMEOUT
      Unmute.SHOW_ON_MUTE = !!SHOW_ON_MUTE
      Unmute.SHOW_IF_NO_SOUND = !!SHOW_IF_NO_SOUND
      Unmute.holdable = holdable ?? true
      Unmute.shiftable = shiftable ?? false
    }
  }

  private initialized = false
  private isActive = false

  public get name(): string {
    return 'unmute'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  private get classNames(): Record<string, string> {
    return Unmute.classNames
  }

  public get template(): TemplateStringifier {
    return template(Unmute.template)
  }

  public get showOnMute(): boolean {
    return Unmute.SHOW_ON_MUTE
  }

  public get shouldBeVisible(): boolean {
    return (
      (Unmute.SHOW_IF_NO_SOUND && this.mediaControl?.hasAudio === false) ||
      !this.core.options.mediaControlVisible
    )
  }

  public get events(): EventMap {
    return {
      'click [data-unmute]': 'toggleMute',
      'mousemove [data-unmute]': 'stopPropagation',
      'mouseenter [data-unmute]': 'cancelTimeout',
      'mouseleave [data-unmute]': 'startTimeout'
    }
  }

  public get attributes(): AttributeMap {
    return {
      class: [
        'eplayer-unmute',
        'eplayer-skin-component',
        Unmute.holdable && 'eplayer-holdable',
        Unmute.shiftable && 'eplayer-skin-shiftable',
        'eplayer-skin-fullsize'
      ]
        .filter(Boolean)
        .join(' ')
    }
  }

  public get i18n(): Strings<T> {
    return this.core.i18n
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  private get notifier(): Notifier<T> | null {
    return this.core.getPlugin('notifier') as Notifier<T> | null
  }

  public bindEvents(): void {
    if (!this.core.isReady) {
      this.onPlay = once(this.onPlay)
    }

    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
    }

    const {activeContainer, activePlayback} = this.core

    if (activeContainer) {
      this.listenToOnce(activeContainer, Events.CONTAINER_PLAY, this.onPlay)
      this.listenTo(
        activeContainer,
        Events.CONTAINER_VOLUME,
        this.onVolumeUpdate
      )
    }

    if (activePlayback) {
      this.listenToOnce(
        activePlayback,
        Events.PLAYBACK_LOADEDDATA,
        this.onLoadedData
      )
    }
  }

  public stopListening(): this {
    this.cancelTimeout()

    return super.stopListening()
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  public stopPropagation(ev: MouseEvent | TouchEvent): void {
    if (Browser.isMobile) ev.stopPropagation()
  }

  private show(): void {
    this.isActive = true
    this.$el.show()
    this.startTimeout()
  }

  private hide(): void {
    this.isActive = false
    this.$el.hide()
    this.cancelTimeout()
  }

  private onLoadedData(): void {
    this.$el.toggleClass(
      this.classNames.noSound,
      this.mediaControl?.hasAudio === false
    )
  }

  private onPlay(): void {
    const {
      autoPlay,
      [PLAY_IN_VISIBLE_AREA]: playInVisibleArea,
      [UNMUTE_IN_VISIBLE_AREA]: unmuteInVisibleArea
    } = this.core.options

    this.initialized = true

    if (
      this.shouldBeVisible ||
      (this.mediaControl?.muted &&
        (this.showOnMute ||
          autoPlay ||
          playInVisibleArea ||
          unmuteInVisibleArea))
    ) {
      this.show()
    }
  }

  private onVolumeUpdate(volume: number): void {
    if (!this.initialized) return

    if (volume > 0) {
      if (!this.shouldBeVisible) this.hide()
    } else if (this.showOnMute) {
      this.show()
    }
  }

  private startTimeout(): void {
    const {SHOW_TIMEOUT} = Unmute

    if (SHOW_TIMEOUT != null) {
      this.showTimeout = window.setTimeout(() => {
        this.hide()
      }, SHOW_TIMEOUT)
    }
  }

  private cancelTimeout(): void {
    clearTimeout(this.showTimeout)
  }

  protected toggleMute(): void {
    const {
      activeContainer,
      options: {volume, mediaControlVisible}
    } = this.core
    const unmute = !this.mediaControl?.volume
    const hasAudio = this.mediaControl?.hasAudio !== false

    activeContainer.setVolume(unmute ? volume ?? Unmute.VOLUME : 0)
    if (!this.shouldBeVisible) this.hide()

    if (Browser.isMobile || !mediaControlVisible || !hasAudio) {
      let phraseKey
      if (hasAudio) phraseKey = unmute ? 'unmuted' : 'muted'
      else phraseKey = 'no_sound'

      this.notifier?.showMessage(
        this.i18n.t(phraseKey),
        undefined,
        mediaControlVisible ? 'top' : 'bottom'
      )
    }
  }

  public render(): this {
    this.$el.html(this.template({unmute: this.i18n.t('unmute')}))
    if (!this.isActive) this.$el.hide()
    this.mediaControl?.$el.append(this.el)

    return this
  }
}
